import { useEffect, useState, useMemo } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Plus, Trash2, Edit2, RefreshCw, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export default function LiveSupportTopics() {
  const [topics, setTopics] = useState([]);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | topic obj
  const [parentForNew, setParentForNew] = useState(null);
  const [collapsed, setCollapsed] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [t, f] = await Promise.all([
        api.get("/admin/live-support/topics"),
        api.get("/admin/live-support/forms"),
      ]);
      setTopics(t.data?.items || []);
      setForms(f.data?.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const tree = useMemo(() => {
    const byParent = {};
    topics.forEach((t) => {
      const k = t.parent_id || "__root__";
      (byParent[k] = byParent[k] || []).push(t);
    });
    Object.values(byParent).forEach((arr) => arr.sort((a, b) => (a.order || 0) - (b.order || 0)));
    return byParent;
  }, [topics]);

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.label}" and all its children?`)) return;
    try {
      await api.delete(`/admin/live-support/topics/${t.id}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const reseed = async () => {
    if (!window.confirm("This will DELETE all existing topics and restore the default BetJili-style tree. Continue?")) return;
    try {
      await api.post("/admin/live-support/topics/reseed");
      toast.success("Default tree restored");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reseed failed"); }
  };

  const renderNode = (t, depth) => {
    const children = tree[t.id] || [];
    const isCollapsed = collapsed[t.id];
    return (
      <div key={t.id}>
        <div className={`flex items-center gap-2 px-3 py-2 hover:bg-emerald-50/40 border-b border-gray-100 ${depth > 0 ? "" : "bg-gray-50/30"}`}
             style={{ paddingLeft: 12 + depth * 22 }}>
          {children.length > 0 ? (
            <button onClick={() => setCollapsed((c) => ({ ...c, [t.id]: !isCollapsed }))} className="text-gray-500 hover:text-gray-900">
              {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
            </button>
          ) : <div className="w-[14px]" />}
          <span className={`text-sm flex-1 ${t.active ? "text-gray-900" : "text-gray-400 line-through"}`}>{t.label}</span>
          {t.form_key && <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-bold">📋 {t.form_key}</span>}
          {t.reply_text && !t.form_key && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-bold">💬 reply</span>}
          <span className="text-[10px] text-gray-400">order: {t.order || 0}</span>
          <button onClick={() => { setParentForNew(t.id); setEditing("new"); }} className="h-7 w-7 rounded hover:bg-emerald-100 text-emerald-700 flex items-center justify-center" title="Add child">
            <Plus size={13} />
          </button>
          <button onClick={() => { setParentForNew(null); setEditing(t); }} className="h-7 w-7 rounded hover:bg-blue-100 text-blue-700 flex items-center justify-center" title="Edit">
            <Edit2 size={13} />
          </button>
          <button onClick={() => remove(t)} className="h-7 w-7 rounded hover:bg-red-100 text-red-600 flex items-center justify-center" title="Delete">
            <Trash2 size={13} />
          </button>
        </div>
        {!isCollapsed && children.map((c) => renderNode(c, depth + 1))}
      </div>
    );
  };

  return (
    <div data-testid="ls-topics-page">
      <PageHeader title="Live Support Topics" subtitle="Configure the multi-level quick-reply tree shown in the chat widget. Click + to add a child topic." />

      <div className="flex items-center justify-between mb-4 gap-2">
        <div className="text-sm text-gray-600">{topics.length} topics</div>
        <div className="flex items-center gap-2">
          <button onClick={reseed} data-testid="ls-topic-reseed"
                  className="h-10 px-3 rounded-sm border border-gray-200 hover:bg-gray-50 text-xs font-semibold inline-flex items-center gap-2">
            <RefreshCw size={13} /> Reset to default tree
          </button>
          <button onClick={() => { setParentForNew(null); setEditing("new"); }} data-testid="ls-topic-add"
                  className="h-10 px-4 rounded-sm bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-sm inline-flex items-center gap-2">
            <Plus size={15} /> New Root Topic
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-sm">
        {loading ? <div className="p-10 text-center text-gray-500">Loading...</div>
         : (tree["__root__"] || []).length === 0 ? <div className="p-10 text-center text-gray-500">No topics yet</div>
         : (tree["__root__"] || []).map((t) => renderNode(t, 0))}
      </div>

      {editing && (
        <TopicEditor
          topic={editing === "new" ? null : editing}
          parentId={parentForNew}
          topics={topics}
          forms={forms}
          onClose={() => { setEditing(null); setParentForNew(null); }}
          onSaved={() => { setEditing(null); setParentForNew(null); load(); }}
        />
      )}
    </div>
  );
}

function TopicEditor({ topic, parentId, topics, forms, onClose, onSaved }) {
  const [label, setLabel] = useState(topic?.label || "");
  const [pid, setPid] = useState(topic ? topic.parent_id || "" : (parentId || ""));
  const [order, setOrder] = useState(topic?.order ?? 0);
  const [formKey, setFormKey] = useState(topic?.form_key || "");
  const [replyText, setReplyText] = useState(topic?.reply_text || "");
  const [active, setActive] = useState(topic?.active ?? true);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!label.trim()) return;
    setSaving(true);
    const body = {
      label: label.trim(),
      parent_id: pid || null,
      order: Number(order) || 0,
      form_key: formKey || null,
      reply_text: replyText || null,
      active,
    };
    try {
      if (topic) await api.put(`/admin/live-support/topics/${topic.id}`, body);
      else await api.post("/admin/live-support/topics", body);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  // Avoid making a topic its own descendant — for now, simple safeguard:
  // exclude the current topic from being its own parent.
  const parentOptions = topics.filter((t) => !topic || t.id !== topic.id);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form onSubmit={submit} className="bg-white rounded-sm shadow-xl w-full max-w-lg p-5" data-testid="ls-topic-editor">
        <div className="flex items-center justify-between mb-4">
          <div className="font-bold text-gray-900">{topic ? "Edit Topic" : "New Topic"}</div>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-800">✕</button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Label (with emoji)</label>
            <input value={label} onChange={(e) => setLabel(e.target.value)} required data-testid="ls-topic-label"
                   placeholder="e.g. 🪙 ডিপোজিট"
                   className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500 text-gray-900 placeholder-gray-400 bg-white" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Parent topic</label>
              <select value={pid} onChange={(e) => setPid(e.target.value)} data-testid="ls-topic-parent"
                      className="w-full h-10 px-2 border border-gray-200 rounded-sm text-sm bg-white text-gray-900">
                <option value="">— No parent (root) —</option>
                {parentOptions.map((t) => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Order</label>
              <input type="number" value={order} onChange={(e) => setOrder(e.target.value)}
                     className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm bg-white text-gray-900" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Attach form (terminal node)</label>
            <select value={formKey} onChange={(e) => setFormKey(e.target.value)} data-testid="ls-topic-form"
                    className="w-full h-10 px-2 border border-gray-200 rounded-sm text-sm bg-white text-gray-900">
              <option value="">— None —</option>
              {forms.map((f) => <option key={f.id} value={f.key}>{f.title} ({f.key})</option>)}
            </select>
            <div className="text-[11px] text-gray-500 mt-1">If set, this topic opens the form instead of showing children.</div>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Bot reply (when clicked, no form/children)</label>
            <textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} rows={3}
                      placeholder="Optional message the bot sends when this topic is selected"
                      className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm bg-white text-gray-900 placeholder-gray-400 resize-none" />
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            <span>Active (visible in widget)</span>
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-gray-100">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-sm border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
          <button type="submit" disabled={saving} data-testid="ls-topic-save"
                  className="h-10 px-4 rounded-sm bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-sm font-bold">
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}
